import * as React from "react"
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography, Avatar, Skeleton, Tooltip } from "@mui/material"
import { TableSeeker, IconSeeker } from "@ui-seeker/core"
import { getSubmit } from "@ui-seeker/utils"

import { BACKEND_APIS } from "@constants"
import { mappingsModal, editMappingDrawer } from "../@signals"


function getRelativeTime(dateString) {
    if (!dateString) return '-'
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`
    return `${Math.floor(diffDays / 365)} years ago`
}

function formatDate(dateString) {
    if (!dateString) return ''
    return new Date(dateString).toLocaleDateString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}


const getColumns = (data, onEdit) => [
    {
        name: 'actions',
        label: 'Actions',
        options: {
            filter: false,
            sort: false,
            setCellProps: () => ({ style: { textAlign: 'center', width: 80 } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value, tableMeta) => {
                const rowIndex = tableMeta.currentTableData?.[tableMeta.rowIndex]?.index
                const rowData = rowIndex !== undefined ? data[rowIndex] : null
                if (!rowData) return null
                
                return (
                    <Tooltip title="Edit mapping">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onEdit(rowData)}
                        >
                            <IconSeeker icon="pen-to-square" />
                        </IconButton>
                    </Tooltip>
                )
            }
        }
    },
    {
        name: 'product_id',
        label: 'Product ID',
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value, tableMeta) => {
                const rowData = tableMeta.tableData[tableMeta.rowIndex]
                const url = rowData?.product_url
                if (url) {
                    return (
                        <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                            {value}
                        </a>
                    )
                }
                return value || '-'
            }
        }
    },
    {
        name: 'product_url',
        label: 'URL',
        options: {
            filter: false,
            sort: false,
            display: false
        }
    },
    {
        name: 'id_site',
        label: 'Site ID',
        options: {
            filter: false,
            sort: false,
            display: false
        }
    },
    {
        name: 'site',
        label: 'Site',
        options: {
            filter: true,
            filterType: 'checkbox',
            sort: true,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } })
        }
    },
    {
        name: 'first_seen',
        label: 'First Seen',
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value) => value ? (
                <Tooltip title={formatDate(value)} arrow>
                    <span style={{ cursor: 'default' }}>{getRelativeTime(value)}</span>
                </Tooltip>
            ) : '-'
        }
    },
    {
        name: 'last_seen',
        label: 'Last Seen',
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value) => value ? (
                <Tooltip title={formatDate(value)} arrow>
                    <span style={{ cursor: 'default' }}>{getRelativeTime(value)}</span>
                </Tooltip>
            ) : '-'
        }
    },
    {
        name: 'image_url',
        label: 'Image',
        options: {
            filter: false,
            sort: false,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value) => (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Avatar
                        src={value}
                        variant="rounded"
                        sx={{ width: 50, height: 50 }}
                    >
                        -
                    </Avatar>
                </Box>
            )
        }
    },
    {
        name: 'last_price',
        label: 'Last Price',
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value, tableMeta) => {
                if (!value) return '-'
                const rowData = tableMeta.tableData[tableMeta.rowIndex]
                const currency = rowData?.last_currency || '€'
                return `${value.toFixed(2)} ${currency}`
            }
        }
    },
    {
        name: 'last_currency',
        label: 'Currency',
        options: {
            filter: false,
            sort: false,
            display: false
        }
    },
    {
        name: 'product_name',
        label: 'Product Name',
        options: {
            filter: false,
            sort: true,
            setCellProps: () => ({ style: { textAlign: 'center' } }),
            setCellHeaderProps: () => ({ style: { textAlign: 'center' } }),
            customBodyRender: (value, tableMeta) => {
                const rowData = tableMeta.tableData[tableMeta.rowIndex]
                const subtitle = rowData?.subtitle
                const variantRaw = rowData?.variant_raw
                return (
                    <Box sx={{ maxWidth: 300 }}>
                        <Typography variant="body2" noWrap>
                            {value || '-'}
                        </Typography>
                        {subtitle && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {subtitle}
                            </Typography>
                        )}
                        {variantRaw && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                {variantRaw}
                            </Typography>
                        )}
                    </Box>
                )
            }
        }
    }
]


export default function MappingsModal() {
    const [data, setData] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const modalData = mappingsModal.value

    React.useEffect(() => {
        if (modalData?.id_sku) {
            setLoading(true)
            getSubmit(`${BACKEND_APIS.SKU_MANAGEMENT}/mappings?id_sku=${modalData.id_sku}`)
                .then(res => {
                    console.log('Mappings API response:', res)
                    setData(res?.data || [])
                })
                .catch(() => setData([]))
                .finally(() => setLoading(false))
        }
    }, [modalData?.id_sku])

    const handleClose = () => {
        mappingsModal.value = null
        setData([])
    }

    const handleEdit = (mapping) => {
        console.log('handleEdit - mapping:', mapping)
        console.log('handleEdit - modalData:', modalData)
        console.log('handleEdit - id_brand:', modalData.id_brand || modalData.brand_id)
        
        editMappingDrawer.value = {
            rawProduct: {
                product_id: mapping.product_id,
                id_site: mapping.id_site,
                site: mapping.site,
                product_name: mapping.product_name,
                image_url: mapping.image_url,
                id_brand: modalData.id_brand || modalData.brand_id
            },
            currentSku: modalData.id_sku ? {
                id_sku: modalData.id_sku,
                name: modalData.name
            } : null
        }
        
        console.log('handleEdit - editMappingDrawer.value:', editMappingDrawer.value)
    }

    const handleMappingUpdated = (updatedMapping) => {
        setData(prevData => prevData.map(item => {
            if (item.product_id === updatedMapping.product_id && item.id_site === updatedMapping.id_site) {
                return { ...item, ...updatedMapping }
            }
            return item
        }))
    }

    if (!modalData) return null

    return (
        <Dialog 
            open={!!modalData} 
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h6">SKU Mappings</Typography>
                    <Typography variant="body2" color="text.secondary">
                        ID SKU: {modalData.id_sku} - {modalData.name}
                    </Typography>
                    {modalData.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                            {modalData.subtitle}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={handleClose} size="small">
                    <IconSeeker icon="xmark" />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <Skeleton variant="rectangular" height={300} />
                ) : data.length === 0 ? (
                    <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center', 
                        justifyContent: 'center',
                        py: 6,
                        color: '#9ca3af'
                    }}>
                        <IconSeeker icon="link-slash" sx={{ fontSize: 40, mb: 2 }} />
                        <Typography variant="body1">No mappings found for this SKU</Typography>
                    </Box>
                ) : (
                    <TableSeeker
                        data={data}
                        columns={getColumns(data, handleEdit)}
                        options={{
                            selectableRows: 'none',
                            pagination: true,
                            rowsPerPage: 10,
                            rowsPerPageOptions: [10, 25, 50],
                            elevation: 0,
                            print: false,
                            download: false,
                            viewColumns: false
                        }}
                    />
                )}
            </DialogContent>
        </Dialog>
    )
}
